import useEvidenceSections from 'components/forms/evidence-field/use-evidence-sections';
import { Checkbox, Group, NumberInput, Select, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconDatabasePlus,
  IconDatabaseX,
  IconPlus,
  IconTrashX,
} from '@tabler/icons-react';
import FormInputGroup from 'components/forms/FormInputGroup';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import { FormProps } from 'components/forms/FormProps';
import FormWrapper from 'components/forms/FormWrapper';
import OrganizationStructureScope from 'components/forms/OrganizationStructureScope';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import TextareaWithCharacterCount from 'components/inputs/TextareaWithCharacterCount';
import { DeviceTypeMultiSelect } from 'components/selects/DeviceTypeSelect';
import Toast from 'components/Toast';
import P1Regular from 'components/typography/P1Regular';
import useLoadingAction from 'hooks/use-loading-action';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { Fragment, useMemo } from 'react';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import { PredefinedDataTypeSelect } from 'components/selects/PredefinedDataTypeSelect';

/**
 * The data collected from the form.
 */
export interface EvidenceFieldFormData {
  sectionId: string;
  order: number;
  fieldName: string;
  fieldDescription: string;
  fieldType: string;
  isRequired: boolean;
  appliesToDevices: boolean;
  appliesToPeople: boolean;
  precision?: number;
  selectOptions: string[];
  predefinedDataTypeId?: string;
  allOrganizations: boolean;
  selectedOrganizations: string[];
  allDeviceTypes: boolean;
  selectedDeviceTypes: string[];
}

const EVIDENCE_FIELD_FORM_INITIAL_VALUES: EvidenceFieldFormData = {
  sectionId: '',
  order: 0,
  fieldName: '',
  fieldDescription: '',
  fieldType: 'text',
  isRequired: false,
  appliesToDevices: true,
  appliesToPeople: false,
  selectOptions: [],
  predefinedDataTypeId: undefined,
  allOrganizations: false,
  selectedOrganizations: [],
  allDeviceTypes: false,
  selectedDeviceTypes: [],
};

/**
 * Parameters of the EvidenceFieldForm.
 */
export interface EvidenceFieldFormProps extends FormProps<EvidenceFieldFormData> {
  primaryButtonText?: string;
  primaryIcon?: React.ReactNode;
}

/**
 * Form for creating and editing evidence fields.
 */
export default function EvidenceFieldForm({
  initialValues = EVIDENCE_FIELD_FORM_INITIAL_VALUES,
  onSubmit = noop,
  primaryButtonText = 'Uložiť',
  primaryIcon = <IconCheck stroke="1.5" height={24} width={24} />,
}: EvidenceFieldFormProps) {
  const fieldTypes = useMemo(
    () => [
      { value: 'text', label: 'Text' },
      { value: 'number', label: 'Číslo' },
      { value: 'date', label: 'Dátum' },
      { value: 'select', label: 'Výber' },
      { value: 'predefined', label: 'Preddefinované údaje' },
      { value: 'checkbox', label: 'Zaškrtávacie pole' },
    ],
    []
  );

  const sectionLayout = useEvidenceSections();
  const { isTablet } = useResponsiveDesign();
  const [{ loading }, submit] = useLoadingAction(onSubmit);

  const form = useForm({
    initialValues: { ...EVIDENCE_FIELD_FORM_INITIAL_VALUES, ...initialValues },
    validate: {
      sectionId: (value, values) =>
        sectionLayout.sections.some(
          (section) =>
            String(section.sectionId) === value && section.module === (values.appliesToPeople ? 'people' : 'devices')
        )
          ? null
          : 'Vyberte sekciu.',
      order: (value) =>
        Number.isInteger(value) && value >= -2147483648 && value <= 2147483647 ? null : 'Zadajte celočíselné poradie.',
      fieldName: createValidator([required]),
      selectedOrganizations: (value, values) => {
        if (!values.allOrganizations && value.length === 0) {
          return 'Musíte vybrať aspoň jednu organizáciu.';
        }
        return null;
      },
      selectedDeviceTypes: (value, values) => {
        if (values.appliesToDevices && !values.allDeviceTypes && value.length === 0) {
          return 'Musíte vybrať aspoň jeden typ zariadenia.';
        }
        return null;
      },
      selectOptions: (value, values) => {
        if (values.fieldType !== 'select') return null;
        const trimmed = value.map((option) => option.trim());
        if (trimmed.length === 0 || trimmed.some((option) => !option)) {
          return 'Pridajte aspoň jednu neprázdnu možnosť.';
        }
        if (new Set(trimmed.map((option) => option.toLocaleLowerCase())).size !== trimmed.length) {
          return 'Možnosti sa nesmú opakovať.';
        }
        return null;
      },
      predefinedDataTypeId: (value, values) =>
        values.fieldType === 'predefined' && !value ? 'Vyberte typ preddefinovaných údajov.' : null,
    },
  });

  return (
    <FormWrapper
      onSubmit={form.onSubmit((values) => {
        if (!sectionLayout.loading && !sectionLayout.error) return submit(values);
        return undefined;
      })}
      loading={loading || sectionLayout.loading}
      primaryButtonText={primaryButtonText}
      primaryIcon={primaryIcon}
      skipSecondaryButtonConfirm={!form.isDirty()}
    >
      {sectionLayout.error && (
        <Toast type="fail" message="Sekcie sa nepodarilo načítať. Obnovte stránku." withCloseButton={false} />
      )}
      <FormInputGroup groupTitle="Základné info">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          <TextInput
            size="lg"
            label="Názov evidenčného poľa"
            placeholder="Názov evidenčného poľa"
            name="fc297234-ff69-4fac-9b85-2bc2b1354e0c"
            withAsterisk
            {...form.getInputProps('fieldName')}
          />
          <Select
            size="lg"
            label="Typ evidenčného poľa"
            placeholder="Vyberte typ evidenčného poľa"
            name="3d20e42e-1152-4461-a7cf-2d2a80ff2b80"
            data={fieldTypes}
            withAsterisk
            {...form.getInputProps('fieldType')}
          />
          <Select
            size="lg"
            label="Použitie evidenčného poľa"
            data={[
              { value: 'devices', label: 'Zariadenia' },
              { value: 'people', label: 'Osoby' },
            ]}
            value={form.values.appliesToPeople ? 'people' : 'devices'}
            onChange={(value) => {
              form.setFieldValue('sectionId', '');
              form.setFieldValue('appliesToDevices', value === 'devices');
              form.setFieldValue('appliesToPeople', value === 'people');
              if (value === 'people') {
                form.setFieldValue('allDeviceTypes', false);
                form.setFieldValue('selectedDeviceTypes', []);
              }
            }}
            withAsterisk
          />
          <Select
            size="lg"
            label="Sekcia"
            withAsterisk
            searchable
            data={sectionLayout.sections
              .filter((section) => section.module === (form.values.appliesToPeople ? 'people' : 'devices'))
              .map((section) => ({ value: String(section.sectionId), label: section.name }))}
            {...form.getInputProps('sectionId')}
          />
          <NumberInput
            size="lg"
            label={
              <Group spacing={4} noWrap>
                Poradie
                <QuestionMarkTooltip
                  withinPortal
                  multiline
                  width={260}
                  label="Vyššie číslo sa zobrazí skôr v rámci sekcie."
                />
              </Group>
            }
            precision={0}
            min={-2147483648}
            max={2147483647}
            {...form.getInputProps('order')}
          />
          <Checkbox
            size="lg"
            label="Povinné pole"
            mt={28}
            styles={{ inner: { marginTop: 0 } }}
            {...form.getInputProps('isRequired', { type: 'checkbox' })}
          />
        </SimpleGrid>
        <TextareaWithCharacterCount
          size="lg"
          label="Popis evidenčného poľa (nepovinné)"
          placeholder="Popis evidenčného poľa"
          name="7f0e9832-2c5b-49a6-86b0-0e73c250b102"
          autosize
          minRows={2}
          maxLength={255}
          {...form.getInputProps('fieldDescription')}
        />
      </FormInputGroup>

      <FormInputGroup groupTitle="Konfigurácia typu">
        <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
          {form.values.fieldType === 'number' ? (
            <NumberInput
              size="lg"
              label="Počet desatinných miest"
              min={0}
              max={8}
              {...form.getInputProps('precision')}
            />
          ) : form.values.fieldType === 'select' ? (
            <Stack spacing={12}>
              {form.values.selectOptions.map((option, index) => (
                <Group key={index} noWrap align="flex-start">
                  <TextInput
                    size="lg"
                    label={`Možnosť ${index + 1}`}
                    value={option}
                    error={form.errors.selectOptions}
                    onChange={(event) => form.setFieldValue(`selectOptions.${index}`, event.currentTarget.value)}
                    sx={{ flex: 1 }}
                  />
                  <Group spacing={4} mt={30} noWrap>
                    <ActionIcon
                      aria-label="Posunúť možnosť nahor"
                      disabled={index === 0}
                      onClick={() => form.reorderListItem('selectOptions', { from: index, to: index - 1 })}
                    >
                      <IconArrowUp />
                    </ActionIcon>
                    <ActionIcon
                      aria-label="Posunúť možnosť nadol"
                      disabled={index === form.values.selectOptions.length - 1}
                      onClick={() => form.reorderListItem('selectOptions', { from: index, to: index + 1 })}
                    >
                      <IconArrowDown />
                    </ActionIcon>
                    <ActionIcon
                      variant="danger-secondary"
                      aria-label="Odstrániť možnosť"
                      onClick={() => form.removeListItem('selectOptions', index)}
                    >
                      <IconTrashX />
                    </ActionIcon>
                  </Group>
                </Group>
              ))}
              <Button
                variant="secondary"
                leftIcon={<IconPlus />}
                onClick={() => form.insertListItem('selectOptions', '')}
              >
                Pridať možnosť
              </Button>
              {form.errors.selectOptions && <P1Regular color="red.8">{form.errors.selectOptions}</P1Regular>}
            </Stack>
          ) : form.values.fieldType === 'predefined' ? (
            <PredefinedDataTypeSelect
              usableForEvidence
              size="lg"
              label="Typ preddefinovaných údajov"
              placeholder="Vyberte typ"
              withAsterisk
              {...form.getInputProps('predefinedDataTypeId')}
            />
          ) : (
            <Toast
              type="info"
              message="Pre tento typ poľa neexistujú žiadne možnosti konfigurácie."
              fullWidth
              withCloseButton={false}
            />
          )}
        </SimpleGrid>
      </FormInputGroup>

      <OrganizationStructureScope
        allOrganizations={form.values.allOrganizations}
        selectedOrganizations={form.values.selectedOrganizations}
        allOrganizationsText="Pole dostupné pre všetky organizácie."
        error={form.errors.selectedOrganizations}
        onAllOrganizationsChange={(value) => form.setFieldValue('allOrganizations', value)}
        onSelectedOrganizationsChange={(value) => form.setFieldValue('selectedOrganizations', value)}
      />

      {form.values.appliesToDevices && (
        <FormInputGroup groupTitle="Vymedzenie zariadení">
          {form.values.allDeviceTypes ? (
            <Group spacing={8} align="center">
              <P1Regular>Pole dostupné pre všetky typy zariadení.</P1Regular>
              <Button
                variant="subtle"
                leftIcon={<IconDatabaseX />}
                onClick={() => form.setFieldValue('allDeviceTypes', false)}
              >
                Zrušiť
              </Button>
            </Group>
          ) : (
            <Fragment>
              <DeviceTypeMultiSelect
                name="selectedDeviceTypes"
                label="Vyberte typy zariadení"
                placeholder="Vyberte typy zariadení"
                withAsterisk
                listSubtypes
                {...form.getInputProps('selectedDeviceTypes')}
              />
              <Group spacing={8} mt={-12}>
                <P1Regular>Alebo</P1Regular>
                <Button
                  variant="subtle"
                  leftIcon={<IconDatabasePlus />}
                  onClick={() => form.setFieldValue('allDeviceTypes', true)}
                >
                  Použiť pre všetky typy zariadení
                </Button>
              </Group>
            </Fragment>
          )}
        </FormInputGroup>
      )}
    </FormWrapper>
  );
}
